import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { TableComponent } from 'src/app/shared/table/components/table/table.component';
import {
  CryptotokenlistActions,
  selectCryptotokenlistGridData,
} from '../../cryptotokenlist/cryptotokenlist.feature';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ColDef } from 'ag-grid-enterprise';
import { CryptoToken } from 'src/app/shared/entities/crypto-token';
import {
  ColumnFilter,
  DataRequest,
  GridSort,
} from 'src/app/shared/table/components/table/table.types';
import { LangService } from 'src/app/shared/services/lang/lang.service';
import { SpinnerComponent } from '../../../../shared/components/spinner/spinner.component';
import { DialogService } from 'src/app/shared/dialog/services/dialog/dialog.service';
import { externalTextColumnFilter } from 'src/app/shared/table/components/filters/external-text-column-filter';
import { PageService } from 'src/app/shared/page/service/page.service';
import { Page } from 'src/app/shared/page/page.config';
import { MatButtonModule } from '@angular/material/button';
import { IconMenuConfig } from 'src/app/shared/table/components/cell-renderers/icon-menu-renderer/icon-menu-renderer.types';
import { IconMenuRendererComponent } from 'src/app/shared/table/components/cell-renderers/icon-menu-renderer/icon-menu-renderer.component';
import { CryptotokenDataService } from 'src/app/shared/store/features/cryptotoken-data/cryptotoken-data.service';
import { TickCrossRendererComponent } from 'src/app/shared/table/components/cell-renderers/tick-cross-renderer/tick-cross-renderer.component';
import { DropdownFloatingFilterComponent } from 'src/app/shared/table/components/filters/dropdown-floating-filter/dropdown-floating-filter.component';
import { CryptotokenListColId } from '../../cryptotokenlist.types';
import { YesNo } from 'src/app/shared/util/yes-no';
import { DropdownFilterComponent } from 'src/app/shared/table/components/filters/dropdown-filter/dropdown-filter.component';
import { TotalComponent } from '../../../../shared/components/total/total.component';
import { catchError, map, filter, finalize, of, switchMap, tap } from 'rxjs';
import { ToastrService } from 'src/app/shared/services/toastr/toastr-service';
import { extractErrorMessage } from 'src/app/shared/util/extract-error-message';
import { UserSelector } from 'src/app/shared/store/user/user.feature';

@Component({
  selector: 'app-cryptotokenlist-main-table',
  imports: [TableComponent, SpinnerComponent, MatButtonModule, TotalComponent],
  templateUrl: './main-table.component.html',
  styleUrl: './main-table.component.scss',
})
export class MainTableComponent {
  private destroyRef = inject(DestroyRef);
  private toastrService = inject(ToastrService);
  private langService = inject(LangService);
  private dialogService = inject(DialogService);
  private pageService = inject(PageService);
  private cryptotokenDataService = inject(CryptotokenDataService);
  labels = this.langService.lang.features.cryptotokenlist;
  private lang = this.langService.lang;
  private store = inject(Store);
  isEditor = toSignal(this.store.select(UserSelector.selectIsEditor));
  private gridData$ = this.store.select(selectCryptotokenlistGridData);
  gridData = toSignal(this.gridData$, {
    initialValue: {
      filter: [],
      sort: GridSort.none,
      rowData: [],
      loading: false,
      paginationState: {
        currentPage: 0,
        totalRecords: 0,
        recordsPerPage: 0,
      },
    },
  });
  total = toSignal(
    this.cryptotokenDataService.data$.pipe(map(data => data?.length)),
  );
  private isDeleting = toSignal(this.cryptotokenDataService.isDeleting);
  isActivating = signal(false);
  isDeactivating = signal(false);
  spinning = computed(
    () =>
      this.gridData().loading ||
      this.isDeleting() ||
      this.isActivating() ||
      this.isDeactivating(),
  );

  private activate(ct: CryptoToken) {
    const tokenName = ct.tokenName;
    if (tokenName) {
      this.dialogService
        .input({
          ...this.labels.actions.activate.dialogConfig,
          isPassword: true,
        })
        .pipe(
          tap(() => {
            this.isActivating.set(true);
          }),
          switchMap(password =>
            password === null
              ? of()
              : this.cryptotokenDataService.activate(ct, password),
          ),
          catchError(err => {
            this.toastrService.error(
              extractErrorMessage(err) ||
                this.labels.actions.activate.defaultErrorMessage,
            );
            return of();
          }),
          finalize(() => {
            this.isActivating.set(false);
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
  }

  private deactivate(ct: CryptoToken) {
    const tokenName = ct.tokenName;
    if (tokenName) {
      this.dialogService
        .confirm({
          text: this.labels.actions.deactivate.confirmation,
        })
        .pipe(
          filter(confirmed => confirmed),
          tap(() => {
            this.isDeactivating.set(true);
          }),
          switchMap(() => this.cryptotokenDataService.deactivate(ct)),
          catchError(err => {
            this.toastrService.error(
              extractErrorMessage(err) ||
                this.labels.actions.activate.defaultErrorMessage,
            );
            return of();
          }),
          finalize(() => {
            this.isDeactivating.set(false);
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
  }

  private actionButtons: IconMenuConfig<CryptoToken> = {
    buttons: [
      {
        icon: 'add_circle_outline',
        tooltip: this.labels.actions.activate.tooltip,
        showIf: ct => !ct.active && !ct.autoActivation,
        callback: data => this.activate(data),
      },
      {
        icon: 'block',
        tooltip: this.labels.actions.deactivate.tooltip,
        showIf: ct => !!ct.active && !ct.autoActivation,
        callback: data => this.deactivate(data),
      },
      {
        icon: 'delete',
        tooltip: this.labels.actions.delete.tooltip,
        callback: data => {
          this.dialogService.withConfirmation({
            text: this.labels.actions.delete.confirmation(data.tokenName ?? ''),
          })(() => this.cryptotokenDataService.delete(data));
        },
      },
    ],
  };

  private dataColumnDefs: ColDef<CryptoToken>[] = [
    {
      colId: CryptotokenListColId.tokenName,
      headerName: this.labels.headers.tokenName,
      field: 'tokenName',
      cellClass: ['link-color', 'curPointer'],
      sortable: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: [externalTextColumnFilter],
      },
      onCellClicked: event => {
        const key = event.data?.tokenName;
        if (key) {
          this.store.dispatch(CryptotokenlistActions.drilldown({ key }));
        }
      },
    },
    {
      colId: CryptotokenListColId.active,
      headerName: this.labels.headers.active,
      field: 'active',
      sortable: false,
      maxWidth: 120,
      filter: DropdownFilterComponent,
      floatingFilter: true,
      floatingFilterComponent: DropdownFloatingFilterComponent,
      floatingFilterComponentParams: {
        options: [
          {
            value: YesNo.Yes,
            label: this.lang.shared.yesNo[YesNo.Yes],
          },
          {
            value: YesNo.No,
            label: this.lang.shared.yesNo[YesNo.No],
          },
        ],
      },
      cellRenderer: TickCrossRendererComponent,
    },
    {
      colId: CryptotokenListColId.autoActivation,
      headerName: this.labels.headers.autoActivation,
      field: 'autoActivation',
      sortable: false,
      maxWidth: 160,
      filter: DropdownFilterComponent,
      floatingFilter: true,
      floatingFilterComponent: DropdownFloatingFilterComponent,
      floatingFilterComponentParams: {
        options: [
          {
            value: YesNo.Yes,
            label: this.lang.shared.yesNo[YesNo.Yes],
          },
          {
            value: YesNo.No,
            label: this.lang.shared.yesNo[YesNo.No],
          },
        ],
      },
      cellRenderer: TickCrossRendererComponent,
    },
  ];

  private actionColumnDef = {
    colId: CryptotokenListColId.actions,
    sortable: false,
    minWidth: 50,
    maxWidth: 120,
    cellRenderer: IconMenuRendererComponent,
    valueGetter: () => this.actionButtons,
  };

  private editorColumnDefs = [...this.dataColumnDefs, this.actionColumnDef];

  columnDefs = computed(() => {
    const isEditor = this.isEditor();
    return isEditor ? this.editorColumnDefs : this.dataColumnDefs;
  });

  onDataRequest(event: DataRequest) {
    this.store.dispatch(CryptotokenlistActions.dataRequest(event));
  }

  onFilterChanged(event: ColumnFilter) {
    this.store.dispatch(CryptotokenlistActions.applyFilter({ filter: event }));
  }

  onSortChanged(event: GridSort) {
    this.store.dispatch(CryptotokenlistActions.applySorting({ sort: event }));
  }

  onCreate() {
    this.pageService.goToPage(Page.CreateCryptotoken);
  }
}
